import { makeAutoObservable, observable, ObservableMap, values } from 'mobx'
import { TrackedChanges } from 'stores/TrackedChanges'
import { type RootStore } from './RootStore'
import { CommentableType } from 'components/ActivityEvent/Types'
import { createUseStore } from './util'
import { CleanupMode } from './CleanupMode'
import { CollabItemsFilters, resolvedFilter } from './CollabItemsFilters'
import {
  CollabItem,
  CollabItemType,
  CommentChannelMessage,
  CommentItem,
  DocMode,
  TodoItem,
  TodoChannelMessage,
  TrackedChangeItem,
} from './DocStore.types'
import { layersKeys } from 'hooks/queries/layers/layersKeys'
import { Query, QueryKey } from 'react-query'
import { isEqual } from 'lodash'
import { AlmanacFileSummary, ArticleView, Layer } from 'types'
import { DecisionModeStore } from './DecisionModeStore'
import { WebsocketConsumer } from 'providers/WebsocketProvider'
import { almanacFileKeys } from 'hooks/queries/almanacFiles/almanacFileKeys'
import { ignoreFirstCall } from 'utils'
import { DocRightRailStore } from './DocRightRailStore'
import { useContext } from 'react'
import { AuthStore } from './AuthStore'
import { CollabItemsStore } from 'stores/CollabItemsStore'
import { CommentsStore } from 'proseShared/plugins/Comments/Types'
import { ArticleClient } from 'api/clients/ArticleClient'
import { RevisionSuggestionRequest } from 'stores/RevisionSuggestionRequest'
import { GenerateSuggestionRequest } from 'stores/GenerateSuggestionRequest'
import { ImageStore } from 'stores/ImageStore'

export class DocStore implements CommentsStore {
  articleId: string
  layerId?: string
  rootStore: RootStore
  trackedChangesStore: TrackedChanges
  cleanupModeStore: CleanupMode
  decisionModeStore: DecisionModeStore
  rightRailStore: DocRightRailStore
  filters: CollabItemsFilters
  commentsRealtimeSubscription: any
  todosRealtimeSubscription: any
  docMode: DocMode = DocMode.Editor
  observedQueries: ObservableMap<QueryKey, any> = observable.map({})
  articleView: ArticleView | null = null
  lastActiveLayerId?: string
  websocketConnection: WebsocketConsumer
  auth?: AuthStore | null
  collabItemsStore: CollabItemsStore
  isGeneratingSuggestion?: boolean
  isFocused = false
  imageStores: ObservableMap<string, ImageStore> = observable.map({})

  _layers: ObservableMap<string, Layer | undefined> = observable.map({})
  _almanac_file?: AlmanacFileSummary

  constructor(
    rootStore: RootStore,
    articleId: string,
    fetchOnInit: boolean = true,
    websocketConnection: WebsocketConsumer = null,
    collabItemsStore?: CollabItemsStore,
  ) {
    makeAutoObservable(this)
    this.articleId = articleId
    this.websocketConnection = websocketConnection
    this.rootStore = rootStore
    this.rootStore.docStore = this
    this.trackedChangesStore = new TrackedChanges({ docStore: this })
    this.cleanupModeStore = new CleanupMode(this)
    this.decisionModeStore = new DecisionModeStore(this)
    this.rightRailStore = new DocRightRailStore(this)
    this.filters = new CollabItemsFilters(this)
    this.collabItemsStore = collabItemsStore || new CollabItemsStore()
    this.isGeneratingSuggestion = undefined // Needs to be initialized as undefined

    if (fetchOnInit) {
      this.fetchCollabItems()
    }
    this.subscribeToRealtimeChannels()
    const almanacFileQueryKey = almanacFileKeys.detail(articleId)
    this.observeQuery<{ almanac_file: AlmanacFileSummary }>(almanacFileQueryKey, this.updateAlmanacFileCache)
    const layersQueryKey = layersKeys.list(articleId, {})
    this.observeQuery<{ layers: Layer[] }>(layersQueryKey, this.updateLayersCache)
  }

  addImageStore = (blockId: string, imageStore: ImageStore) => {
    this.imageStores.set(blockId, imageStore)
  }

  focus() {
    this.isFocused = true
  }

  blur() {
    this.isFocused = false
  }

  setAuth(auth: AuthStore | null) {
    this.auth = auth
  }

  setWebsocketConnection(connection: WebsocketConsumer) {
    this.websocketConnection = connection
    this.subscribeToRealtimeChannels()
  }

  fetchCollabItems() {
    if (!this.articleId) return

    this.rootStore.comments.load(this.articleId)
    this.rootStore.todos.load(this.articleId)
  }

  destroy() {
    if (this.trackedChangesStore.docChanged) {
      this.trackArticleUpdate()
    }
    this.rightRailStore.destroy()
    this.unsubscribeFromRealtimeChannels()
    if (this.rootStore.docStore === this) {
      this.rootStore.docStore = undefined
    }
    this.rootStore.layout.setRightColumnLocked(false)
  }

  setTrackedChanges(trackedChanges: TrackedChanges) {
    this.trackedChangesStore.select(undefined)
    this.trackedChangesStore = trackedChanges
  }

  observeQuery<T>(queryKey: QueryKey, callback: (query: Query<T>) => void) {
    const cache = this.rootStore.queryClient.getQueryCache()
    const existingQuery = cache.find<T>(queryKey)
    if (existingQuery) {
      callback(existingQuery)
    }
    this.observedQueries.set(
      queryKey,
      cache.subscribe((event) => {
        if (!isEqual(event?.query.queryKey, queryKey) || event?.type !== 'queryUpdated') return
        callback(event.query)
      }),
    )
  }

  trackArticleUpdate() {
    ArticleClient.trackArticleUpdate(this.articleId)
  }

  setArticleId(articleId: string) {
    this.articleId = articleId
    this.setDocMode(DocMode.Editor)
    this.fetchCollabItems()
    this.subscribeToRealtimeChannels()
    const almanacFileQueryKey = almanacFileKeys.detail(this.articleId)
    this.observeQuery<{ almanac_file: AlmanacFileSummary }>(almanacFileQueryKey, this.updateAlmanacFileCache)
    const layersQueryKey = layersKeys.list(this.articleId, {})
    this.observeQuery<{ layers: Layer[] }>(layersQueryKey, this.updateLayersCache)
  }

  setIsGeneratingSuggestion(isGeneratingSuggestion: boolean) {
    this.isGeneratingSuggestion = isGeneratingSuggestion
  }

  /* -------------------------------------------------------------------------------------------------
   * Collab Items
   * -----------------------------------------------------------------------------------------------*/
  setLayerId(layerId: string) {
    this.layerId = layerId
  }

  private updateLayersCache = (query: Query<{ layers: Layer[] }>) => {
    if (query.state.data) {
      query.state.data.layers.forEach((layer) => {
        this._layers.set(layer.id, layer)
      })
    }
  }

  private updateAlmanacFileCache = (query: Query<{ almanac_file: AlmanacFileSummary }>) => {
    if (query.state.data) {
      this._almanac_file = query.state.data.almanac_file
    }
  }

  setOrderedCollabItemsUuids = (uuids: Set<string>) => {
    this.collabItemsStore.setOrderedCollabItemsUuids(uuids)
  }

  setDocMode(mode: DocMode) {
    if (mode === DocMode.Cleanup) {
      this.trackedChangesStore.setDocStore(this)
      this.cleanupModeStore.resetCounters()
      this.rightRailStore.setLocked(true)
    }

    this.docMode = mode
  }

  filterByResolvedOrNotResolved(item: CollabItem) {
    if (this.filters.activeKeys.has('resolved')) {
      return resolvedFilter.fn(item)
    } else {
      return !resolvedFilter.fn(item)
    }
  }

  get inCleanupMode() {
    return this.docMode === DocMode.Cleanup
  }

  get comments() {
    if (!this.layerId) return []

    return this.rootStore.comments
      .getByCommentable(this.articleId, CommentableType.ARTICLE)
      .filter(({ layer_id }) => layer_id === this.layerId)
  }

  get todos() {
    if (!this.layer?.is_main) return []
    if (this.articleView === ArticleView.Commenting) return []

    return this.rootStore.todos.getByArticle(this.articleId)
  }

  get trackedChanges() {
    if (this.articleView === ArticleView.Commenting) return []

    return this.trackedChangesStore.changes
  }

  get collabItems(): CollabItem[] {
    return [
      ...this.comments.map((comment) => ({ type: CollabItemType.Comment, item: comment } as CommentItem)),
      ...this.todos.map((todo) => ({ type: CollabItemType.Todo, item: todo } as TodoItem)),
      ...this.trackedChanges.map(
        (change) => ({ type: CollabItemType.TrackedChange, item: change } as TrackedChangeItem),
      ),
    ]
  }

  // collab items filtered by the active filters and by either 'resolved'
  // if resolved filter is active or by 'not resolved' if resolved filter not active
  get filteredCollabItems(): CollabItem[] {
    return this.filteredActiveCollabItems.filter((item) => this.filterByResolvedOrNotResolved(item))
  }

  // collab items filtered by the active filters
  get filteredActiveCollabItems(): CollabItem[] {
    return this.collabItems.filter((item) => this.filters.active.every((filter) => filter.fn(item)))
  }

  // collab items filtered by active non-user filters and by either 'resolved'
  // if resolved filter is active or by 'not resolved' if resolved filter not active
  get filteredActiveNonUserCollabItems(): CollabItem[] {
    return this.collabItems
      .filter((item) => this.filters.activeNonUser.every((filter) => filter.fn(item)))
      .filter((item) => this.filterByResolvedOrNotResolved(item))
  }

  get pinnedCollabItems(): CollabItem[] {
    return this.filteredCollabItems.filter(({ item, type }) => type !== CollabItemType.TrackedChange && item.important)
  }

  get docWideCollabItems(): CollabItem[] {
    return this.filteredCollabItems
      .filter(({ item, type }) => type !== CollabItemType.TrackedChange && !item.important && !item.uuid)
      .concat(this.inlineDetachedCollabItems)
  }

  get inlineCollabItems(): CollabItem[] {
    return this.filteredCollabItems.filter(
      ({ item, type }) => Boolean(item.uuid) && (type === CollabItemType.TrackedChange || !item.important),
    )
  }

  get inlineAttachedCollabItems(): CollabItem[] {
    return Array.from(this.collabItemsStore.orderedCollabItemsUuids)
      .map((uuid) => this.inlineCollabItems.find(({ item }) => item.uuid === uuid))
      .filter((item) => item !== undefined) as CollabItem[]
  }

  get inlineDetachedCollabItems(): CollabItem[] {
    return this.inlineCollabItems.filter(
      ({ item }) => item.uuid && !this.collabItemsStore.orderedCollabItemsUuids.has(item.uuid),
    )
  }

  get orderedFilteredCollabItems(): CollabItem[] {
    return [...this.pinnedCollabItems, ...this.docWideCollabItems, ...this.inlineAttachedCollabItems] as CollabItem[]
  }

  get orderedCollabItemsUuids() {
    return this.collabItemsStore.orderedCollabItemsUuids
  }

  /* -------------------------------------------------------------------------------------------------
   * Realtime updates
   * -----------------------------------------------------------------------------------------------*/

  subscribeToRealtimeChannels = () => {
    this.subscribeToCommentsRealtimeChannel()
    this.subscribeToTodosRealtimeChannel()
  }

  unsubscribeFromRealtimeChannels = () => {
    this.commentsRealtimeSubscription?.unsubscribe()
    this.todosRealtimeSubscription?.unsubscribe()
  }

  subscribeToCommentsRealtimeChannel = () => {
    this.commentsRealtimeSubscription?.unsubscribe()

    if (!this.websocketConnection) return

    this.commentsRealtimeSubscription = this.websocketConnection.subscriptions.create(
      { channel: 'CommentChannel', article_id: this.articleId },
      {
        connected: ignoreFirstCall(() => {
          this.rootStore.comments.load(this.articleId)
        }),
        received: (payload: CommentChannelMessage) => {
          const { comment, action, comment_id } = payload
          if (action === 'remove') {
            this.rootStore.comments.remove(comment_id)
            return
          }
          if (action === 'add') {
            if (this.auth?.id !== comment.creator.id && !this.rootStore.comments.comments.has(comment.id)) {
              comment.read_status = false
            }
            this.rootStore.comments.add(comment)
          }
        },
      },
    )
  }

  subscribeToTodosRealtimeChannel = () => {
    this.todosRealtimeSubscription?.unsubscribe()

    if (!this.websocketConnection) return

    this.todosRealtimeSubscription = this.websocketConnection.subscriptions.create(
      { channel: 'TodoChannel', article_id: this.articleId },
      {
        connected: ignoreFirstCall(() => {
          this.rootStore.todos.load(this.articleId)
        }),
        received: (payload: TodoChannelMessage) => {
          const { todo, action, todo_id } = payload
          if (action === 'remove') {
            this.rootStore.todos.remove(todo_id)
            return
          }
          if (action === 'add') {
            this.rootStore.todos.add(todo)
          }
        },
      },
    )
  }

  setArticleView = (articleView: ArticleView) => {
    this.articleView = articleView
    if (articleView === ArticleView.Reading) {
      this.rightRailStore.setLocked(false)
    }
  }

  setLastActiveLayerId = (layerId: string | undefined) => {
    this.lastActiveLayerId = layerId
  }

  get layerStatus() {
    return this.layer?.status
  }

  get isMainLayer() {
    return this.layer?.is_main
  }

  get mainLayerId() {
    return this.mainLayer?.id
  }

  get permissions() {
    return this.layer?.permissions
  }

  get isVersionControlled() {
    return this._almanac_file?.is_version_controlled || false
  }

  get owner() {
    return this.mainLayer?.owner
  }

  get completedAISuggestionRequest() {
    if (!this.layer?.completed_ai_suggestion_request) return null

    if (this.layer?.completed_ai_suggestion_request.type === 'generate') {
      return new GenerateSuggestionRequest(this.layer?.completed_ai_suggestion_request)
    }

    return new RevisionSuggestionRequest(this.layer?.completed_ai_suggestion_request)
  }

  private get layer(): Layer | undefined {
    if (!this.layerId) return undefined
    return this._layers.get(this.layerId)
  }

  private get mainLayer(): Layer | undefined {
    return values(this._layers).find((layer) => layer && layer.is_main)
  }

  highlight(uuid: string | undefined) {
    return this.collabItemsStore.highlight(uuid)
  }

  get highlightedUuid() {
    return this.collabItemsStore.highlightedUuid
  }

  select(uuid: string | undefined) {
    return this.collabItemsStore.select(uuid)
  }

  get selectedUuid() {
    return this.collabItemsStore.selectedUuid
  }
}

export const [DocStoreContext, useDocStore] = createUseStore(DocStore)

export const useOptionalDocStore = () => useContext(DocStoreContext)
